/**
 * The prerendered page, kept on screen until React has drawn its own.
 *
 * Every public page arrives as HTML (scripts/prerender.mjs), but React starts
 * with createRoot, not hydrateRoot (src/index.jsx says why), and the pages are
 * lazy routes: its first commit showed the route's Suspense fallback, a spinner,
 * in place of the whole page until the page's chunk arrived. On a phone the
 * prerendered headline painted at 4 s, vanished at 6.4 s and came back at 7.2 s,
 * and the footer jumped up the empty page and back (0.32 CLS: every mobile URL
 * "poor" in Search Console). Showing the prerendered page itself as that first
 * fallback means nothing moves: what arrived stays until React replaces it with
 * the same thing.
 */
let snapshot = null;

// The element App.tsx wraps the routes in; `display: contents` keeps it out of layout
export const ROUTE_ROOT_ATTR = 'data-route-root';

/** Read the prerendered route before React clears the root. Call once, first. */
export function capturePrerenderedPage(doc = document, pathname = window.location.pathname) {
  const el = doc.querySelector(`[${ROUTE_ROOT_ATTR}]`);
  snapshot = el && el.childElementCount > 0 ? { path: pathname, html: el.innerHTML } : null;
}

/** The prerendered HTML of this path, if the page was loaded on it. */
export function prerenderedHtml(pathname) {
  return snapshot && snapshot.path === pathname ? snapshot.html : null;
}

/**
 * A framer-motion entrance (`initial`) for content that was prerendered: none on
 * the page the visit landed on, since that content is already on screen, and
 * fading it out to fade it back in would blink the headline and push its paint
 * (the LCP) back by the length of the fade. Every later mount animates as before.
 */
export function entrance(initial) {
  return snapshot && typeof window !== 'undefined' && snapshot.path === window.location.pathname ? false : initial;
}
